import { useCallback, useMemo } from 'react';
import { TaskStatus } from '@/types/tasks.ts';
import { DocumentWithDownloadUrl as Document, PendingDocumentShareResponse } from '@/client';
import { makeLogger } from '@/utils/logger/logger.ts';
import useHouseDocuments from './useHouseDocuments.ts';
import { uploadSelectors } from '@/utils/uploadItemUtils.ts';
import {
	CRITICAL_INFORMATION_TAG,
	RecordTag,
	USER_DOCUMENT_LINK_TAG,
	USER_NOTE_TAG,
} from '@/constants/documentTags.ts';
import { useHouseTaskManager } from '@/context/house/tasks/HouseTaskManagerContext.tsx';
import { CONCURRENT_TASK_TYPES } from '@/context/house/tasks/types.ts';
import { isUploadComplete } from '@/utils/documentUtils';

const logger = makeLogger('useUploadedRecords');
const NON_GENERIC_RECORD_TAGS: RecordTag[] = [USER_NOTE_TAG, USER_DOCUMENT_LINK_TAG, CRITICAL_INFORMATION_TAG];

/**
 * Time-to-live for document ID mappings (5 seconds)
 */
const ID_MAPPING_TTL = 5000;

export type UploadStatus = 'uploading' | 'completed' | 'failed';

export interface UploadedRecord {
	id: string;
	filename: string;
	status: UploadStatus;
	/** Always present: upload progress is represented by status/uploaded_at, never by a document-less record. */
	document: Document;
	isRecent: boolean;
	progress?: number | null;
	pendingDocument?: PendingDocumentShareResponse;
}

interface UploadedRecordsOptions {
	refetchInterval?: number | false;
}

/**
 * Creates an UploadedRecord from a Document
 * Uses previous ID for stable React keys during ID transitions
 */
const createFromDocument = (doc: Document): UploadedRecord => {
	// Check if this document was recently updated and has a previous ID
	// Use the previous ID for the React key to prevent unmount/remount during transition
	let stableId = doc.id;

	// Check if any previous ID maps to this new ID
	for (const [prevId, newId] of documentIdMappings.entries()) {
		if (newId === doc.id) {
			stableId = prevId;
			break;
		}
	}

	return {
		id: `doc-${stableId}`,
		filename: doc.filename,
		status: isUploadComplete(doc) ? 'completed' : 'uploading',
		document: doc,
		isRecent: true,
	};
};

/**
 * Module-level Map for document ID mappings (previousId → newId)
 * Tracks recently updated documents to maintain stable React keys
 * Using a module-level Map instead of useRef to avoid hook rules violations
 */
const documentIdMappings = new Map<number, number>();

/**
 * Registers a document ID swap (old ID → new ID) with automatic cleanup after TTL
 */
export const registerDocumentIdSwap = (previousId: number, newId: number) => {
	logger.debug('Registering swap:', previousId, '→', newId);
	documentIdMappings.set(previousId, newId);

	// Clear mapping after TTL
	setTimeout(() => {
		if (documentIdMappings.get(previousId) === newId) {
			logger.debug('Clearing expired mapping:', previousId, '→', newId);
			documentIdMappings.delete(previousId);
		}
	}, ID_MAPPING_TTL);
};

/**
 * Provides a declarative, documents-only view of uploaded records for a house
 * so UI can render a single, consistent list without managing task lifecycles.
 *
 * Why: We centralized task UI in UploadQueueOverlay. Pages/components should
 * only read existing records and simple visibility rules. This hook derives
 * a presentational list from house documents and hides records that have an
 * active delete queued via HouseTaskManager.
 */
export const useUploadedRecords = (houseId: number | null, options: UploadedRecordsOptions = {}) => {
	const { documents, refetch, isLoading, isError } = useHouseDocuments(houseId, options);
	const { uiTasks } = useHouseTaskManager();

	// Convert documents to presentational items using module-level ID mapping
	const uploadItems: UploadedRecord[] = useMemo(() => {
		const documentItems = documents.map((doc) => createFromDocument(doc));
		return documentItems;
	}, [documents]);

	// No sorting here - preserve API order
	// Documents are already ordered by the API
	// Sorting will be handled in useHouseRecords for pending documents only

	// Deduplicate by document id
	const finalItems = useMemo(() => {
		const seenDocumentIds = new Set<number>();
		const dedupedItems: UploadedRecord[] = [];

		uploadItems.forEach((item) => {
			const documentId = item.document.id;
			if (seenDocumentIds.has(documentId)) {
				return;
			}

			seenDocumentIds.add(documentId);
			dedupedItems.push(item);
		});

		return dedupedItems;
	}, [uploadItems]);

	const getItemsWithTag = useCallback(
		(tagName: string) => uploadSelectors.filterByTag(finalItems, tagName),
		[finalItems],
	);
	const getItemsWithoutTag = useCallback(
		(tagNames: RecordTag[]) => uploadSelectors.filterWithoutTag(finalItems, tagNames),
		[finalItems],
	);
	// Hide docs that have an active delete task in the UI task manager
	const activeDeleteDocIds = useMemo(() => {
		return new Set(
			uiTasks
				.filter((t) => t.taskType === CONCURRENT_TASK_TYPES.DELETE_RECORD)
				.filter((t) => {
					const s = t.execution.apiTask.status;
					return s !== TaskStatus.Completed && s !== TaskStatus.Failed;
				})
				.map((t) => (t.execution.apiTask.metadata as Record<string, unknown>)?.documentId as number),
		);
	}, [uiTasks]);

	// Track docs that have an active update task in the UI task manager
	// Include both current and previous document IDs to handle ID transitions
	const activeUpdateDocIds = useMemo(() => {
		const idSet = new Set<number>();

		uiTasks
			.filter((t) => t.taskType === CONCURRENT_TASK_TYPES.UPDATE_RECORD)
			.filter((t) => {
				const s = t.execution.apiTask.status;
				return s !== TaskStatus.Completed && s !== TaskStatus.Failed;
			})
			.forEach((t) => {
				const meta = t.execution.apiTask.metadata as Record<string, unknown>;
				const currentId = meta?.documentId as number;
				const previousId = meta?.previousDocumentId as number;

				// Track both current and previous IDs during transition
				if (currentId) idSet.add(currentId);
				if (previousId) idSet.add(previousId);
			});

		return idSet;
	}, [uiTasks]);

	const visibleUploadItems = useMemo(
		() => finalItems.filter((item) => !activeDeleteDocIds.has(item.document.id)),
		[finalItems, activeDeleteDocIds],
	);
	const uploadItemsGeneric = useMemo(
		() => uploadSelectors.filterWithoutTag(finalItems, NON_GENERIC_RECORD_TAGS),
		[finalItems],
	);
	const visibleUploadItemsGeneric = useMemo(
		() => uploadSelectors.filterWithoutTag(visibleUploadItems, NON_GENERIC_RECORD_TAGS),
		[visibleUploadItems],
	);

	return {
		uploadItems: finalItems,
		uploadItemsGeneric,
		visibleUploadItems,
		visibleUploadItemsGeneric,
		refetch,
		isLoading,
		isError,
		activeUpdateDocIds,
		// Selectors
		getItemsWithTag,
		getItemsWithoutTag,
	};
};

export default useUploadedRecords;
